import _ from 'underscore';
import { DeterministicView } from './deterministic-view';

/**
 * Helper function to update a view's state
 * @param {DeterministicView} view - The view to be updated
 * @param {(Function|Object)} patch -
 *    It could be either a function or an object.
 *    - If it's a funciton, new state will be patch(currentState)
 *    - If it's an object, new state will be _.defaults(patch, currentState)
 * @returns {DeterministicView} - The view being updated
 */
export function updateView(view, patch) {
  if (_.isFunction(patch)) {
    return view.setState(patch(view.getState()));
  }
  return view.setState(_.extend({}, view.getState(), patch));
}

function getFactory(view) {
  return view.__factory || view.constructs; // eslint-disable-line
}

function setFactory(view, Factory) {
  return _.extend(view, { __factory: Factory }); // eslint-disable-line
}

/**
 * Helper function to create a child view on demond. If the child exists, return
 * it directly, otherwise create the view with the given information
 * @param {DeterministicView} view - The parent view
 * @param {string} ref - The ref of the child view
 * @param {Function} viewFactory -
 *    The factory method or constructor of the child view
 *    - If the prototype of viewFactory is instance of DeterministicView, treat
 *      it as a construstor.
 *    - Otherwise, treat it as a factory method.
 * @param {Object} options - The constructor option of the child view
 * @param {Object} events -
 *    The event handlers to be bound to the child view.
 *    - The keys of the object are child view event names.
 *    - The values of the object could be a handler function or a method name of
 *      the parent view.
 * @returns {DeterministicView} - The child view
 */
export function ensureChildView(view, ref, viewFactory, options = {}, events = {}) {
  let childView = view.getChild(ref);

  if (!childView || getFactory(childView) !== viewFactory) {
    if (_.isFunction(viewFactory)) {
      if (viewFactory === DeterministicView
        || viewFactory.prototype instanceof DeterministicView) {
        const ViewConstructor = viewFactory;
        childView = new ViewConstructor(options);
      } else {
        childView = viewFactory(options);
      }
    }

    if (!(childView instanceof DeterministicView)) {
      throw new Error('Invalid viewFactory');
    }

    setFactory(childView, viewFactory);

    _.each(events, (handler, event) => {
      const handlerFunction = _.isString(handler) ? view[handler] : handler;
      if (!_.isFunction(handlerFunction)) {
        throw new Error('Invalid event handler');
      }
      view.listenTo(childView, event, handlerFunction);
    });
    view.setChild(ref, childView);
  }

  return childView;
}
